import { Card, Icon, OverlayTrigger, ProgressRing, colorValue } from '@hai/ui-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import { Labler } from 'src/js/component/base/labler';
import { PushButton } from 'src/js/component/base/push-button';
import { UserAnalytics } from 'src/js/component/base/user-analytics';
import PageHeader from 'src/js/component/page-header';
import systemInfo from 'src/js/data/systemInfo';
import systemStatus, { saveResponseToSystemStatus } from 'src/js/data/systemStatus';
import { triggerEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useStores } from 'src/js/hook/use-stores';
import { mapStatus } from 'src/js/mapper/status-mapper';
import { StatusResponse } from 'src/js/model/api/response/status-response';
import {
  EmsServiceResponse,
  EmsServiceState,
  getEmsStateIcon,
  mapEmsServices,
} from 'src/js/pages/settings/services/ems-models';
import emsService, { saveResponseToEmsService } from 'src/js/pages/settings/services/ems-store';
import { encoderStateToColor, encoderStateToName } from 'src/js/pages/streaming/audio-encoder/audio-encoder-models';
import audioEncoders, { fetchAudioEncoders } from 'src/js/pages/streaming/audio-encoder/audio-encoder-store';
import { formatUptime } from 'src/js/pages/streaming/stream/stream-helper';
import { VideoEncoderViewModel } from 'src/js/pages/streaming/video-encoder/video-encoder-models';
import videoEncoders, { fetchVideoEncoders } from 'src/js/pages/streaming/video-encoder/video-encoder-store';
import SidebarLayout from 'src/js/sidebar';
import { useWatch, useWatchObject } from 'src/js/store/use-watch';
import { generateDataAuto } from 'src/js/util/automation';
import { PRODUCT, constant } from 'src/js/util/constant';
import { toTitleCase } from 'src/js/util/global-util';
import { capitalizeTheFirstLetters } from 'src/js/util/index';

import { DashboardStreamList } from './dashboard-stream-list';
import { fetchStreams } from 'src/js/pages/streaming/stream/stream-store';
import { ServicesResponse } from 'src/js/model/api/response/services-response';

let connectionLost = false;
export const DashboardContainer: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionStore } = useStores();

  const [audEncs, setAudEncs] = useState(audioEncoders);
  useWatchObject(audioEncoders, setAudEncs);

  // TO TEST 4 chips
  // useWatchObject(audioEncoders, (i) => setAudEncs(i.filter((i) => i.id < 4)));

  const [vidEncs, setVidEncs] = useState(videoEncoders);
  useWatchObject(videoEncoders, setVidEncs);

  // let endrun = false; // when true we are unmounted, no set state call should be done

  const fetchSystemStatus = () =>
    apiClientWithoutHandler.genericController.get<StatusResponse>(APIPath.status.get).then((res) => {
      if (res.ok) {
        const response: StatusResponse = res.data;
        if (response) {
          const systemStatus = mapStatus(response);
          saveResponseToSystemStatus(systemStatus);
        }
      }
      return res;
    });

  const [emsServiceActive, setEmsServiceActive] = useState(false);
  const fetchServices = () =>
    apiClientWithoutHandler.genericController.get<any>(APIPath.services.status).then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const services = response as ServicesResponse;
          if (services.ems && services.status?.ems) {
            setEmsServiceActive(true);
          } else {
            setEmsServiceActive(false);
          }
          //saveResponseToServices(services);
        }
      }
      return res;
    });

  const fetchEmsService = () =>
    apiClientWithoutHandler.genericController.get<any>(APIPath.services.ems).then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const emsService = mapEmsServices(response?.data as EmsServiceResponse);
          saveResponseToEmsService(emsService);
        }
      } else {
        saveResponseToEmsService({ state: EmsServiceState.DISABLED });
      }
      return res;
    });

  const interval = constant.defaultRefreshInterval;
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  useAdaptiveFetch(fetchSystemStatus, interval * 3);
  useAdaptiveFetch(fetchServices, interval * 10);
  useAdaptiveFetch(() => fetchVideoEncoders(t), interval * 3);
  useAdaptiveFetch(
    () =>
      fetchStreams(t).then((res) => {
        if (res.ok) {
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
      }),
    interval,
    [refreshTrigger],
  );

  const triggerRefresh = () => {
    setRefreshTrigger((val) => val + 1);
  };

  const [status, setStatus] = useState(systemStatus);
  useWatchObject(systemStatus, setStatus);

  const [emsState, setEmsState] = useState(emsService.state);
  useWatch(emsService, 'state', (s: any) => {
    setEmsState(s);
  });

  useAdaptiveFetch(() => fetchAudioEncoders(t), interval * 3);
  useAdaptiveFetch(
    fetchEmsService,
    emsState !== EmsServiceState.DISABLED || emsServiceActive ? interval * 3 : undefined,
  );

  let audioLengthClass = 'audio-16';
  if (constant.product === PRODUCT.FX) {
    audioLengthClass = 'audio-4'; // for accurate inital load dynamic
  }
  if (audEncs.length <= 4) {
    audioLengthClass = 'audio-4';
  } else if (audEncs.length <= 16) {
    audioLengthClass = 'audio-16';
  } else if (audEncs.length > 16) {
    audioLengthClass = 'audio-32';
  }

  let totalLoad = 0;
  let totalScalingLoad = 0.0;
  let totalScalingLoadRounded = 0;
  const mapVideoUsageSections = (list: VideoEncoderViewModel[], showScalerLoad = false) =>
    list.map((vidEnc) => {
      let scalerLoadVal = Math.round(vidEnc.stats.scalerLoad);
      if (isNaN(scalerLoadVal)) {
        scalerLoadVal = 0;
      }
      if (showScalerLoad) {
        totalScalingLoad += vidEnc.stats.scalerLoad ?? 0.0;
        if (totalScalingLoadRounded + scalerLoadVal > 100) {
          scalerLoadVal = 100 - totalScalingLoadRounded; // prevent donut overflow
        }
        totalScalingLoadRounded += scalerLoadVal;
      } else {
        totalLoad += vidEnc.stats.encoderLoad ?? 0;
      }
      return {
        value: showScalerLoad ? scalerLoadVal : vidEnc.stats.encoderLoad ?? 0,
        color: vidEnc.dashboardColor,
        tooltipText: (
          <>
            <div
              className="tooltipText"
              style={{
                display: 'flex',
                justifyItems: 'center',
                fontSize: '1.5em',
                margin: '5px 0 8px 0',
              }}
            >
              {showScalerLoad ? scalerLoadVal : vidEnc.stats.encoderLoad ?? 0}%
            </div>
            {t('dashboard.video.videoEncoder')} {vidEnc.id}
          </>
        ),
        tooltipBgColor: constant.productColor,
      };
    });

  const videoLoadSections = mapVideoUsageSections(vidEncs, false).filter((enc) => enc.value != 0);
  const videoScalingLoadSections = mapVideoUsageSections(vidEncs, true).filter((enc) => enc.value != 0);

  totalLoad = Math.round(totalLoad);
  if (totalLoad > 100) {
    totalLoad = 100;
  }
  totalScalingLoad = Math.round(totalScalingLoad);
  if (totalScalingLoad > 100) {
    totalScalingLoad = 100;
  }

  return (
    <div id="dashboard">
      <div id="firstRow">
        <Card
          title={t('dashboard.video.title')}
          id="video-encoders-card"
          icon="Encoders"
          className={audioLengthClass}
          titleOnClick={() => navigate('/streaming/videnc')}
        >
          <div className="dashboard-card">
            <Labler label={t('dashboard.video.encoding')} className="text-center">
              {videoLoadSections.length ? <ProgressRing
                label={t('dashboard.video.inUse')}
                currentPercentage={totalLoad}
                sections={videoLoadSections}
              ></ProgressRing> : <ProgressRing
              label={''}
              sections={[{label:'', value:0}]}
            ></ProgressRing>}
            </Labler>
            {constant.product !== PRODUCT.X1 && (
              <Labler label={t('dashboard.video.scaling')} className="text-center">
                {videoScalingLoadSections.length ? <ProgressRing
                  label={t('dashboard.video.inUse')}
                  currentPercentage={totalScalingLoad}
                  sections={videoScalingLoadSections}
                  currentPercentColor={colorValue('haiui-green-01')}
                ></ProgressRing>: <ProgressRing
                label={''}
                sections={[{label:'', value:0}]}
                currentPercentColor={colorValue('haiui-green-01')}
              ></ProgressRing> }
              </Labler>
            )}
            <Labler
              className={`${
                audioLengthClass === 'audio-4' ? 'disappearBelow1100' : 'disappearBelowSmallDesktop'
              } encodersChipsSection`}
              label={t('dashboard.video.videoEncoders')}
            >
              <div className="pushButtonSection video">
                {vidEncs.map((item, key) => (
                  <PushButton
                    data-auto={generateDataAuto('push_button', `video encoder ${key}`)}
                    key={key}
                    href="/streaming/videnc"
                    id={item.id}
                    color={item.dashboardColor}
                    hoverText={capitalizeTheFirstLetters(item.stateString)}
                  >
                    {item.id}
                  </PushButton>
                ))}
              </div>
            </Labler>
          </div>
        </Card>

        <Card
          title={t('dashboard.audio.title')}
          id="audio-encoders-card"
          icon="AudioEncoder"
          className={audioLengthClass}
          titleOnClick={() => navigate('/streaming/audenc')}
        >
          <div className="dashboard-card">
            {audEncs?.length > 0 && (
              <Labler className="encodersChipsSection" label={t('dashboard.audio.header')}>
                <div className={`pushButtonSection audio ${audioLengthClass}`}>
                  {audEncs.map((item, key) => (
                    <PushButton
                      data-auto={generateDataAuto('push_button', `audio encoder ${key}`)}
                      key={key}
                      id={item.id}
                      href="/streaming/audenc"
                      color={encoderStateToColor(item.state)}
                      hoverText={capitalizeTheFirstLetters(encoderStateToName(item.state, t))}
                    >
                      {item.id}
                    </PushButton>
                  ))}
                </div>
              </Labler>
            )}
          </div>
        </Card>

        <Card
          title={t('dashboard.deviceStatus.title')}
          id="device-status-card"
          icon="Information"
          className={audioLengthClass}
          titleOnClick={() => navigate('/settings/system')}
        >
          <div className="text-status-card">
            <OverlayTrigger popoverContent={emsService.emsHostname} placement="top">
              <Labler label={t('dashboard.deviceStatus.emsStatus')} id="ems-state-string">
                <Icon type="status" subtype={getEmsStateIcon(emsState)} size="sm3" />
                {emsState && toTitleCase(emsState)}
              </Labler>
            </OverlayTrigger>
            {constant.product === PRODUCT.FX ? (
              <Labler label={t('dashboard.deviceStatus.activeInput')} className="disappearBelowSmallDesktop">
                {videoEncoders[0]?.inputInterface >= -1
                  ? t(`videnc.enums.inputInterface.${videoEncoders[0]?.inputInterface}`)
                  : ''}
              </Labler>
            ) : constant.product === PRODUCT.X1 ? (
              <></>
            ) : (
              <Labler
                label={t('dashboard.deviceStatus.personality')}
                className="disappearBelowSmallDesktop"
                data-auto={generateDataAuto('label', 'personality')}
              >
                {systemInfo.personality == '2110' ? t('settings.personality.2110') : systemInfo.personality}
              </Labler>
            )}
            <Labler
              mask
              label={t('dashboard.deviceStatus.serialNumber')}
              className={
                audioLengthClass === 'audio-32'
                  ? 'disappearBelowExtraLargeDesktop' /* Audio-32 serial number won't overflow (NKE-2895) */
                  : constant.product === PRODUCT.X1
                  ? 'disappearBelowSmallDesktop'
                  : 'disappearBelowLargeDesktop'
              } /* for X1 show serial number even at mid resolutions, but hide below small desktop */
              data-auto={generateDataAuto('label', 'serial number')}
            >
              {status.serialNumber}
            </Labler>
            <Labler
              label={t('dashboard.deviceStatus.systemUptime')}
              className="tnum"
              data-auto={generateDataAuto('label', 'system uptime')}
            >
              {formatUptime(status.uptime, true)}
            </Labler>
            <Labler
              label={t('dashboard.deviceStatus.firmware')}
              className="disappearBelowSmallDesktop"
              data-auto={generateDataAuto('label', 'firmware')}
            >
              {status.firmwareVersion}
            </Labler>
          </div>
        </Card>
      </div>
      <DashboardStreamList triggerRefresh={triggerRefresh} />
      {sessionStore.isAdmin() && <UserAnalytics />}
    </div>
  );
};

export const Dashboard: React.FunctionComponent = () => {
  const { t } = useTranslation();

  return (
    <SidebarLayout>
      <PageHeader title={t('sidebar.dashboard')} iconName="Dashboard" />
      <DashboardContainer />
    </SidebarLayout>
  );
};
